import {
  Alignment,
  AnchorButton,
  Button,
  Callout,
  Classes,
  Collapse,
  Drawer,
  Intent,
  Label,
  Position,
  Switch,
} from '@blueprintjs/core';
import uniq from 'lodash.uniq';
import * as React from 'react';
import { Route } from 'react-router-dom';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { EditableLabel } from 'src/components/EditableLabel';
import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { PropertyTypeSelector } from 'src/components/PropertyTypeSelector';
import {
  DEFAULT_PROJECT_PROPERTY_TYPE,
  ProjectPropertyType,
} from 'src/constants/project';
import { ROUTE } from 'src/constants/routes';
import { TEST_IDS } from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import {
  getDefaultProjectDataPrefix,
  getDefaultProjectModelPrefix,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { setAllPropertyTypes } from 'src/containers/ProjectDiagram/utils/propertyType/setAllPropertyTypes';
import { projectSelector } from 'src/selectors/project';
import { propertyTypeCountSelector } from 'src/selectors/propertyType';
import { Project } from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import {
  INVALID_IRI_MATCHER,
  IRI_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidIriChars';

const validateName = (name: string) =>
  name ? '' : copy.components.projectSettingsDrawer.validation.nameRequired;

const validateIri = (iri: string): string => {
  if (!iri) {
    return copy.components.projectSettingsDrawer.validation.namespaceRequired;
  }

  if (!iri.match(IRI_STARTS_WITH_MATCHER)) {
    return copy.components.projectSettingsDrawer.validation.namespaceStartsWith;
  }

  if (!iri.includes(':')) {
    return copy.components.projectSettingsDrawer.validation.namespaceSemicolon;
  }

  const invalidChars = iri.match(INVALID_IRI_MATCHER);
  if (invalidChars) {
    return copy.components.projectSettingsDrawer.validation.namespaceChars(
      uniq(invalidChars)
    );
  }

  return '';
};

export const isUseAdvancedSettings = (project?: Project) =>
  project &&
  (project.modelNamespace !== getDefaultProjectModelPrefix(project) ||
    project.dataNamespace !== getDefaultProjectDataPrefix(project) ||
    project.propertyType !== DEFAULT_PROJECT_PROPERTY_TYPE);

// warning should be visible when custom property types will be overwritten
// (when going from custom to yes/no and custom values don't match selection)
export const isPropertyTypeWarningVisible = (
  projectPropertyType: ProjectPropertyType | undefined,
  newPropertyType: ProjectPropertyType,
  isAllPropertyType: ReturnType<
    ReturnType<typeof propertyTypeCountSelector>
  >['isAllPropertyType']
) =>
  newPropertyType !== ProjectPropertyType.CUSTOM &&
  projectPropertyType === ProjectPropertyType.CUSTOM &&
  !isAllPropertyType[newPropertyType];

const ProjectSettingsDrawerBody = () => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const { isAllPropertyType } = useReduxSelector(
    propertyTypeCountSelector(projectId)
  );
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  const [name, setName] = React.useState(project?.name || '');
  const [modelNamespace, setModelNamespace] = React.useState(
    project?.modelNamespace || ''
  );
  const [dataNamespace, setDataNamespace] = React.useState(
    project?.dataNamespace || ''
  );
  const [propertyType, setPropertyType] = React.useState<ProjectPropertyType>(
    project?.propertyType || DEFAULT_PROJECT_PROPERTY_TYPE
  );
  const [useAdvancedSettings, setUseAdvancedSettings] = React.useState(
    isUseAdvancedSettings(project)
  );

  React.useEffect(() => {
    setName(project?.name || '');
    setModelNamespace(project?.modelNamespace || '');
    setDataNamespace(project?.dataNamespace || '');
    setUseAdvancedSettings(isUseAdvancedSettings(project));
  }, [projectId, project]);

  const onSetName = (newName: string) => {
    setName(newName);

    // Update model namespace to use new name if it was using the default value
    if (
      project &&
      project.modelNamespace === getDefaultProjectModelPrefix(project)
    ) {
      setModelNamespace(
        getDefaultProjectModelPrefix({ ...project, name: newName })
      );
    }
    // Update data namespace to use new name if it was using the default value
    if (
      project &&
      project.dataNamespace === getDefaultProjectDataPrefix(project)
    ) {
      setDataNamespace(
        getDefaultProjectDataPrefix({ ...project, name: newName })
      );
    }
  };

  const handleChangeUseAdvancedSettings = () => {
    const nextUseAdvancedSettings = !useAdvancedSettings;
    setUseAdvancedSettings(nextUseAdvancedSettings);
    if (!nextUseAdvancedSettings && project) {
      setModelNamespace(getDefaultProjectModelPrefix({ ...project, name }));
      setDataNamespace(getDefaultProjectDataPrefix({ ...project, name }));
      setPropertyType(DEFAULT_PROJECT_PROPERTY_TYPE);
    }
  };

  const onClose = () => {
    setName('');
    setModelNamespace('');
    setDataNamespace('');
    setUseAdvancedSettings(false);
    dispatch(uiSlice.actions.toggledProjectSettingsDrawer({ isOpen: false }));
  };

  const onSubmit = () => {
    if (project?.name !== name) {
      dispatch(projectsActionCreators.renamedProject({ id: projectId, name }));
    }

    if (project?.modelNamespace !== modelNamespace) {
      dispatch(
        projectsActionCreators.updatedProjectModelNamespace(
          projectId,
          modelNamespace
        )
      );
    }
    if (project?.dataNamespace !== dataNamespace) {
      dispatch(
        projectsActionCreators.updatedProjectDataNamespace(
          projectId,
          dataNamespace
        )
      );
    }

    if (project?.propertyType !== propertyType) {
      dispatch(
        projectsActionCreators.updatedProjectPropertyType(
          projectId,
          propertyType
        )
      );

      if (diagram && propertyType !== ProjectPropertyType.CUSTOM) {
        // overwrite custom properties
        commitDiagramModel(diagram, setAllPropertyTypes(propertyType));

        // clear undo history because project settings can't be undone
        diagram.undoManager.clear();
      }
    }

    onClose();
  };

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.projectSettingsDrawer.container}
      >
        <Label className="sd-drawer-editable-label">
          {copy.components.projectSettingsDrawer.name}
          <div className={Classes.CALLOUT}>
            <EditableLabel
              onSetValue={onSetName}
              onValidate={validateName}
              placeholder={project?.name || ''}
              value={name}
            />
          </div>
        </Label>

        <Switch
          alignIndicator={Alignment.RIGHT}
          checked={useAdvancedSettings}
          className="sd-drawer-use-advanced-settings"
          data-testid={TEST_IDS.projectSettingsDrawer.useAdvancedSettings}
          inline
          label={copy.components.projectSettingsDrawer.useAdvancedSettings}
          onChange={handleChangeUseAdvancedSettings}
        />

        <Collapse isOpen={useAdvancedSettings}>
          <InlineEditableLabel
            label={copy.components.projectSettingsDrawer.modelNamespace}
            onSetValue={setModelNamespace}
            onValidate={validateIri}
            placeholder={project?.modelNamespace || ''}
            value={modelNamespace}
          />
          <InlineEditableLabel
            label={copy.components.projectSettingsDrawer.dataNamespace}
            onSetValue={setDataNamespace}
            onValidate={validateIri}
            placeholder={project?.dataNamespace || ''}
            value={dataNamespace}
          />
          <PropertyTypeSelector
            allowCustom
            propertyType={propertyType}
            setPropertyType={setPropertyType}
          />
        </Collapse>
        <Collapse
          isOpen={isPropertyTypeWarningVisible(
            project?.propertyType,
            propertyType,
            isAllPropertyType
          )}
        >
          <Callout
            data-testid={
              TEST_IDS.projectSettingsDrawer.customPropertyTypeWarning
            }
            intent={Intent.WARNING}
          >
            {copy.components.projectSettingsDrawer.customPropertyTypeWarning}
          </Callout>
        </Collapse>
      </div>
      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectSettingsDrawer.cancelButton}
            intent={Intent.PRIMARY}
            onClick={onClose}
            outlined
            text={copy.components.projectSettingsDrawer.cancel}
          />
          <AnchorButton
            data-testid={TEST_IDS.projectSettingsDrawer.submitButton}
            intent={Intent.PRIMARY}
            onClick={onSubmit}
            text={copy.components.projectSettingsDrawer.submit}
          />
        </div>
      </div>
    </>
  );
};

export const ProjectSettingsDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector(
    (state) => state.ui.isProjectSettingsDrawerOpen
  );

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledProjectSettingsDrawer({ isOpen: false }));
  }, [dispatch]);

  return (
    <Drawer
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-drawer-project-settings"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={copy.components.projectSettingsDrawer.title}
    >
      <Route path={ROUTE.PROJECT}>
        <ProjectSettingsDrawerBody />
      </Route>
    </Drawer>
  );
};
